import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Star, Music, Image as ImgIcon, User, Film, ScrollText,
  BookOpen, Clapperboard, Tv, BookmarkIcon, Play, Eye, ArrowUpRight, Headphones, ChefHat,
  NotebookPen,
} from "lucide-react";
import { api, TYPE_META } from "@/lib/api";

const ICONS = {
  music: Music, picture: ImgIcon, artist: User, video: Film,
  movie: Clapperboard, tvshow: Tv,
  article: ScrollText, book: BookOpen, recipe: ChefHat, unknown: BookmarkIcon,
};

// Quick-action mapping per type — verb + glyph.
const QUICK_ACTION = {
  music:   { label: "Listen", Icon: Headphones },
  picture: { label: "View",   Icon: Eye },
  artist:  { label: "Visit",  Icon: ArrowUpRight },
  video:   { label: "Watch",  Icon: Play },
  movie:   { label: "Watch",  Icon: Play },
  tvshow:  { label: "Watch",  Icon: Play },
  article: { label: "Read",   Icon: BookOpen },
  book:    { label: "Read",   Icon: BookOpen },
  recipe:  { label: "Cook",   Icon: ChefHat },
};

function formatStampDate(d) {
  const dt = new Date(d);
  return dt.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();
}

export default function ItemCard({ item, index = 0, variant = "default" }) {
  const Icon = ICONS[item.type] || BookmarkIcon;
  const meta = TYPE_META[item.type] || TYPE_META.unknown;
  const isPoster = item.type === "movie" || item.type === "tvshow";
  const isVinyl = item.type === "music";
  const isGallery = variant === "gallery" || item.type === "picture";
  const aspectClass = isPoster ? "aspect-[2/3]" : (isVinyl ? "aspect-square" : "aspect-[4/3]");
  const rot = (((item.id || "x").charCodeAt(0) % 5) - 2) * 0.5;

  const action = QUICK_ACTION[item.type];
  const hasSource = Boolean(item.source_url);

  const openSource = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (item.source_url) {
      window.open(item.source_url, "_blank", "noopener,noreferrer");
      // Quietly mark as played — used by Today's Pick to prefer unread items.
      if (!item.played) {
        api.patch(`/items/${item.id}`, { played: true }).catch(() => {});
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10, rotate: rot * 2 }}
      animate={{ opacity: 1, y: 0, rotate: rot }}
      transition={{ duration: 0.65, delay: Math.min(index * 0.04, 0.4), ease: [0.22, 0.61, 0.36, 1] }}
      style={{ "--tape-color": meta.tape }}
    >
      <Link to={`/item/${item.id}`} data-testid={`item-card-${item.id}`} className="group block">
        <article
          className={`placed-card relative overflow-hidden ${isGallery ? "gallery-frame" : ""} ${isVinyl ? "vinyl-sleeve" : ""}`}
          style={{ borderColor: meta.border, background: `linear-gradient(180deg, ${meta.bg}55, var(--card-bg))` }}
        >
          <span className="tape-corner" />

          {/* Vinyl record peeking out — only for music type */}
          {isVinyl && item.image_url && (
            <div className="vinyl-record" aria-hidden="true">
              <div className="vinyl-record-inner" style={{ backgroundImage: `url(${item.image_url})` }}/>
            </div>
          )}

          {item.image_url ? (
            <div className={`${aspectClass} w-full overflow-hidden relative`} style={{ background: meta.bg }}>
              <img
                src={item.image_url}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
              {hasSource && action && (
                <button
                  type="button"
                  onClick={openSource}
                  data-testid={`quick-action-${item.id}`}
                  aria-label={`${action.label} on source`}
                  className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full
                             bg-[var(--card-bg)]/95 backdrop-blur-sm border shadow-md
                             opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0
                             focus:opacity-100 transition-all duration-300"
                  style={{ borderColor: meta.border }}
                >
                  <action.Icon size={12} strokeWidth={1.6} style={{ color: meta.accent }} />
                  <span className="font-cinzel text-[10px] tracking-[0.22em] uppercase" style={{ color: meta.accent }}>
                    {action.label}
                  </span>
                </button>
              )}
            </div>
          ) : (
            <div className={`${aspectClass} w-full flex items-center justify-center`} style={{ background: meta.bg }}>
              <Icon size={52} strokeWidth={1} style={{ color: meta.accent, opacity: 0.5 }} />
            </div>
          )}

          {item.favorite && (
            <Star size={16} className="absolute top-3 right-3 fill-[var(--gold)] text-[var(--gold)]" strokeWidth={1.2}/>
          )}

          {/* Note indicator — small pencil-journal glyph when a private note
              exists on this item. Owner-only data; never shown to anyone else
              because ItemCard only renders in the owner's library views. */}
          {item.note && (
            <span
              aria-label="has a note"
              title="has a note"
              data-testid={`item-note-indicator-${item.id}`}
              className="absolute top-3 left-3 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[var(--card-bg)]/92 backdrop-blur-sm border border-[rgba(140,106,47,0.28)] text-[var(--bronze)] shadow-sm"
            >
              <NotebookPen size={11} strokeWidth={1.8} />
            </span>
          )}

          <div className="p-5 pb-6">
            <div className="flex items-center gap-2 mb-2">
              <Icon size={11} strokeWidth={1.4} style={{ color: meta.accent }} />
              <span className="font-cinzel text-[9px] tracking-[0.26em] uppercase" style={{ color: meta.accent }}>
                {meta.label}
              </span>
            </div>
            <h3 className="font-serif-lit text-xl leading-snug text-[var(--ink)] line-clamp-2">
              {item.title || "Untitled"}
            </h3>
            {item.creator && (
              <p className="mt-1 text-sm text-[var(--ink-dusty)] italic font-garamond line-clamp-1">{item.creator}</p>
            )}
            <div className="mt-3 flex items-center justify-between">
              <span className="font-mono-lc text-[10px] tracking-[0.18em] text-[var(--ink-light)]">
                {formatStampDate(item.date_added)}
              </span>
              {item.tags?.length > 0 && (
                <span className="font-mono-lc text-[10px] tracking-[0.18em] text-[var(--ink-light)]">
                  {item.tags.length} tag{item.tags.length === 1 ? "" : "s"}
                </span>
              )}
            </div>
          </div>
        </article>
      </Link>
    </motion.div>
  );
}
